import React, { useEffect, useRef } from "react";

import { SHOPIFY_DOMAIN } from "@const/urls";

import { HIKO_LOGIN_PROVIDER_KEY } from "@lib/hikoHeadlessSession";
import type { IHikoSigninElement } from "@lib/hikoHeadlessSession";

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

/**
 * jp locale(New Customer Accounts) 전용 소셜 로그인 위젯 UI.
 * HIKO Social Login Plus(signin-headless)를 사용 — 구버전 SocialLogin.tsx(hiko-auth-headless.js)와는
 * 완전히 다른 API. 두 앱은 별개이며, ko/en은 계속 구버전(SocialLogin.tsx)을 사용한다.
 *
 * 로그인 완료 처리는 여기서 하지 않는다 — 위젯이 소셜 로그인을 항상 Shopify 호스티드 페이지를 거치는
 * 풀 리다이렉트로 처리해서, 리다이렉트-백 시점엔 이 컴포넌트(및 `hiko:login` 리스너)가 이미
 * 언마운트된 상태다. 실제 로그인 반영은 앱 부팅 시 App.tsx의 syncHikoSessionOnBoot()가 담당한다.
 */
export function SocialLoginCaa() {
  const container = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<IHikoSigninElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let revealObserver: MutationObserver | null = null;
    let revealFallbackTimer: number | null = null;

    const mount = async () => {
      await import("signin-headless");
      if (cancelled || !container.current) return;

      // "hs-lbl" 텍스트에서 "Continue with " 접두어를 빼고 브랜드명만 노출("Google" 등).
      window.__hikoSignin = { messages: { continueWith: "{provider}" } };

      const el = document.createElement("hiko-signin") as unknown as IHikoSigninElement;
      el.setAttribute("shop", JP_SHOP_DOMAIN);
      container.current.appendChild(el);
      widgetRef.current = el;

      // hiko-signin은 Lit의 open Shadow DOM에 렌더링되므로 index.css 같은 전역 스타일은 닿지 않음
      const style = document.createElement("style");
      style.textContent = `
        :host, .hs, .hs * {
          font-family: "thisisneverthat", "Pretendard Variable", "Pretendard JP Variable", sans-serif !important;
        }
        .hs {
          display: flex !important;
          flex-direction: column !important;
          margin: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        .hs-social {
          order: 2 !important;
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: wrap !important;
          gap: 0.25rem !important;
          padding-top: 2.5rem !important;
        }
        .hs-social .hs-btn {
          width: calc((100% - (0.25rem * 2)) / 3) !important;
          box-sizing: border-box !important;       
        }
        
        .hs-social::before {
          content: "Social Login" !important;
          display: block !important;
          width: 100% !important;             /* 가로 전체 차지하여 버튼들 위로 띄움 */
          font-size: 0.875rem !important;
          line-height: 1.33 !important;        /* leading-[133%] */
          margin-bottom: 0.25rem !important;   /* gap-1 효과 */
          cursor: pointer !important;
        }
        
        /* 이메일 폼 컨테이너를 세로 방향(flex-col)으로 설정 */
        .hs-email {
          order: 1 !important;
          display: flex !important;
          flex-direction: column !important;
          order: 1 !important;
          margin-bottom: 0.5rem !important; 
        }
        
        /* 이메일 폼 상단에 Label 문구 생성 */
        .hs-email::before {
          content: "Login" !important;
          display: block !important;
          font-size: 0.875rem !important;
          margin-bottom: 1.25rem !important;
          cursor: pointer !important;
        }
        
        /* 이메일 폼 하단 안내 문구 추가 */
        .hs-email::after {
          content: "メールでのログイン方法が変更されました。\\A Shopifyから既存のアカウントにログインできます。" !important;
          white-space: pre-wrap !important;
          display: block !important;
          margin-top: 0.375rem !important;
          font-size: 0.875rem !important;
          line-height: 1.33 !important;
          font-weight: 700;
          color: #666666 !important;
        }
        
        /* 1. 컨테이너 패딩 및 배경 초기화 */
        .hs-email-field {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          border: none !important;
          gap: 0px !important;
          padding: 0px !important;
        }
        
        .hs-email-field::before {
          content: "Email Address" !important;
          width: 100% !important;
          display: block !important;
          font-size: 0.875rem !important;
          line-height: 1.33 !important;
          font-weight: 700 !important;
          color: #000000 !important;
          margin-bottom: 0.25rem !important; /* input과의 간격 (gap-1) */
          cursor: pointer !important;
          order: 1 !important;
        }
        
        /* input 요소 스타일링 (py-1.5 구현) */
        .hs-email-field input {
          order: 2 !important;
          flex: 1 !important;
          width: 100% !important;
          min-width: 0 !important;
          outline: none !important;
          background: transparent !important;
          
          padding-top: 0.375rem !important;
          padding-bottom: 0.375rem !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          
          margin-bottom: 0.375rem !important;
          
          font-size: 0.875rem !important;    
          color: #000000 !important;
          
          border: none !important;
          border-bottom: 1px solid #000000 !important;
          border-radius: 0 !important;
                  
          background: transparent !important; 
        }
        
        /* placeholder:text-grey (기본 플레이스홀더 색상 설정) */
        .hs-email-field input::placeholder {
          color: #888888 !important;
          font-weight: 700 !important;
          opacity: 1 !important;
          font-size: 0.875rem !important;
        }
        
        /* 우측 화살표 제출 버튼 */
        .hs-email-field button {
          order: 3 !important;
          width: 100% !important;
          background: black !important;
          border: none !important;
          border-radius: 0 !important;
          padding: 0 0.25rem !important;
          cursor: pointer !important;
          font-size: 0 !important;
          line-height: 2rem !important;
          color: white !important;
        }
        
        /* 버튼에 "Login" 텍스트 추가 */
        .hs-email-field button::after {
          content: "Continue with Shopify" !important;
          color: #ffffff !important;
          font-size: 0.875rem !important;      /* 원하는 Login 폰트 크기 */
          line-height: 1.33 !important;         /* leading-[133%] */
          font-weight: 700 !important;
        }
        
        /* pc: 반응형 대응 (예: min-width 1024px 기준) */
        @media (min-width: 1024px) {
          .hs-email::before,
          .hs-social::before {
            font-size: clamp(1.1rem, 1.25vw, 1.25rem) !important;
            line-height: normal !important;
          }
        }
      `;
      el.shadowRoot?.appendChild(style);

      // signin-headless 0.2.0의 공식 이벤트 — el.login()이 호출되는 즉시(리다이렉트 전) 발생하며
      // event.detail.provider에 어떤 방식으로 로그인을 시도했는지 담겨 온다(소셜은 provider명 문자열,
      // 이메일은 null). DOM 텍스트를 긁던 이전 방식보다 안정적이라 이걸로 대체.
      const captureLoginProvider = (event: Event) => {
        const provider = (event as CustomEvent<{ provider: string | null }>).detail?.provider;
        sessionStorage.setItem(HIKO_LOGIN_PROVIDER_KEY, provider ?? "email");
      };

      el.addEventListener("hiko:loginstart", captureLoginProvider);

      // config fetch 완료 전에는 기본값(이메일 인풋만)으로 먼저 렌더링되고, fetch가 끝나야 소셜 버튼이 붙은 최종 UI로 다시 렌더링됨(FOUC).
      // 소셜 버튼이 실제로 나타날 때까지 위젯을 숨겨서 그 깜빡임을 감춤.
      // config fetch 실패/무프로바이더 대비 타임아웃 포함.
      el.style.visibility = "hidden";

      const revealWidget = () => {
        el.style.visibility = "visible";
        revealObserver?.disconnect();
        revealObserver = null;
        if (revealFallbackTimer !== null) {
          window.clearTimeout(revealFallbackTimer);
          revealFallbackTimer = null;
        }
      };

      if (el.shadowRoot) {
        revealObserver = new MutationObserver(() => {
          if (el.shadowRoot?.querySelector(".hs-social .hs-btn")) revealWidget();
        });
        revealObserver.observe(el.shadowRoot, { childList: true, subtree: true });
      }
      revealFallbackTimer = window.setTimeout(revealWidget, 3000);
    };

    void mount();

    return () => {
      cancelled = true;
      revealObserver?.disconnect();
      if (revealFallbackTimer !== null) window.clearTimeout(revealFallbackTimer);
      widgetRef.current = null;
      if (container.current) container.current.innerHTML = "";
    };
  }, []);

  return <div ref={container} />;
}
